import { ThemeEvents, CartAddEvent } from '@theme/events';
import { confirmPlantLimit } from '@theme/us-plant-limit';

/**
 * Surreal Succulents spend-rewards progress bar and free RARE plant picker.
 *
 * `snippets/succ-progress.liquid` renders every bar (cart page, cart drawer, product page and the
 * "Added to cart" notification) with the shopper's reward ladder, so a GBP cart paints correctly
 * before this loads. This module keeps every bar in step with the cart:
 *
 * - Each `<succ-progress>` renders itself from its `data-subtotal`, `data-claimed` and
 *   `data-currency` attributes and the ladder in its JSON config. Horizon's cart morph refreshes
 *   those attributes for the bars inside a cart section (the bar carries `data-skip-subtree-update`,
 *   so the morph leaves its children to us); every cart event refreshes them for all bars from
 *   `/cart.js`.
 * - The tiers are sterling. For any other presentment currency they are converted with Shopify's own
 *   rate and rounded up to a whole unit, so the amount shown is the amount compared.
 * - The picker (`snippets/succ-free-plant-picker.liquid`, once per page) opens from a bar's "Choose"
 *   link, and by itself right after a cart change that earns the plant, unless the shopper already
 *   closed it this session.
 */

const ELEMENT_NAME = 'succ-progress';
const PICKER_SELECTOR = 'dialog[data-succ-free-plant]';
const OPEN_SELECTOR = '[data-succ-free-plant-open]';
const COPY_SELECTOR = '[data-succ-progress-copy]';
const STORAGE_KEY = 'succ-free-plant:dismissed';
const COPIED_DURATION = 2000;

/**
 * @typedef {{ amount: number, label: string, reward: string }} Tier
 * @typedef {{
 *   tiers: Tier[],
 *   plantTier: number,
 *   codeTier: number,
 *   hasPicks: boolean,
 *   freebieProductIds: number[],
 *   currency: string,
 *   locale: string,
 *   messages: Record<'next' | 'complete' | 'earned' | 'value' | 'reached' | 'locked', string>
 * }} Config
 * @typedef {{ amounts: number[], reached: number, fill: number, remaining: number, owed: boolean, codeUnlocked: boolean }} State
 */

/**
 * A sterling threshold (pence) in the cart's currency (minor units), rounded up to a whole unit.
 * @param {number} gbpCents
 * @param {number} rate
 */
export function toLocal(gbpCents, rate) {
  return Math.ceil((gbpCents * rate) / 100) * 100;
}

/**
 * Where the cart stands on the ladder. Mirrors the Liquid in `snippets/succ-progress.liquid`.
 * @param {Config} config
 * @param {number} subtotal - `items_subtotal_price`, in the cart's minor units.
 * @param {boolean} claimed - Whether the free plant is already in the cart.
 * @param {number} rate - Shopify's presentment rate (1 for GBP).
 * @returns {State}
 */
export function progressState(config, subtotal, claimed, rate) {
  const amounts = config.tiers.map((tier) => toLocal(tier.amount, rate));
  const count = amounts.length;
  const reached = amounts.filter((amount) => subtotal >= amount).length;

  let fill = 100;
  let remaining = 0;
  if (reached < count) {
    const previous = reached > 0 ? amounts[reached - 1] : 0;
    const next = amounts[reached];
    fill = ((reached + Math.max(0, subtotal - previous) / (next - previous)) / count) * 100;
    remaining = next - subtotal;
  }

  return {
    amounts,
    reached,
    fill: Math.round(fill * 100) / 100,
    remaining,
    owed: config.hasPicks && reached >= config.plantTier && !claimed,
    codeUnlocked: reached >= config.codeTier,
  };
}

/**
 * Whether the cart already holds the free plant: a line the picker added, or anything from the
 * picks collection. Mirrors the Liquid.
 * @param {{ items?: Array<{ product_id: number, properties?: Record<string, unknown> | null }> }} cart
 * @param {number[]} productIds
 */
export function isClaimed(cart, productIds) {
  const ids = new Set(productIds.map(Number));
  return (cart.items ?? []).some(
    (item) => item.properties?._freebie === 'true' || ids.has(Number(item.product_id))
  );
}

/** Shopify's rate for the page's presentment currency; 1 when the market prices in GBP. */
function currencyRate() {
  const rate = Number.parseFloat(window.Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

/**
 * @param {number} cents
 * @param {string} currency
 * @param {string} locale
 * @param {boolean} [whole] - Drop the decimals for a whole amount (the milestone labels).
 */
function formatMoney(cents, currency, locale, whole = false) {
  const amount = cents / 100;
  const options = whole && Number.isInteger(amount) ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {};
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, ...options }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`);
}

/**
 * Fills `[key]` placeholders. The templates and the config strings come from the theme's locale
 * file (already escaped by Liquid's `t`); callers escape anything computed here.
 * @param {string} template
 * @param {Record<string, string>} values
 */
function fillTemplate(template, values) {
  return template.replace(/\[(\w+)\]/g, (match, key) => values[key] ?? match);
}

/** @param {string} html */
function normalizeHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.innerHTML;
}

/**
 * Plain text from escaped HTML, for attributes. A <template> keeps it inert.
 * @param {string} html
 */
function htmlToText(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content.textContent ?? '';
}

function wasDismissed() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function rememberDismissed() {
  try {
    sessionStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Storage blocked (private mode): the picker may open by itself again, which is harmless.
  }
}

class SuccProgress extends HTMLElement {
  static observedAttributes = ['data-subtotal', 'data-claimed', 'data-currency'];

  static #resizeObserver = new ResizeObserver((entries) => {
    for (const { target, contentRect } of entries) {
      if (target instanceof SuccProgress) target.#onResize(contentRect.width);
    }
  });

  /** @type {Config | null | undefined} */
  #config;

  #updating = false;

  /** @type {number | undefined} */
  #width;

  connectedCallback() {
    startListening(this);
    this.render();
    SuccProgress.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    SuccProgress.#resizeObserver.unobserve(this);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected && !this.#updating) this.render();
  }

  /** @returns {Config | null} */
  get config() {
    if (this.#config === undefined) {
      try {
        this.#config = JSON.parse(this.querySelector('script[data-succ-progress-config]')?.textContent ?? 'null');
      } catch {
        this.#config = null;
      }
    }
    return this.#config ?? null;
  }

  get subtotal() {
    return Number(this.dataset.subtotal) || 0;
  }

  get claimed() {
    return this.dataset.claimed === 'true';
  }

  /** @returns {State | null} */
  get state() {
    const { config } = this;
    return config ? progressState(config, this.subtotal, this.claimed, currencyRate()) : null;
  }

  /**
   * Takes a fresh cart state and renders once.
   * @param {{ subtotal: number, claimed: boolean, currency?: string }} data
   */
  update({ subtotal, claimed, currency }) {
    this.#updating = true;
    this.dataset.subtotal = String(subtotal);
    this.dataset.claimed = String(claimed);
    if (currency) this.dataset.currency = currency;
    this.#updating = false;
    this.render();
  }

  render() {
    const { config, state } = this;
    if (!config || !state) return;

    const { messages, tiers } = config;
    const currency = this.dataset.currency || config.currency;
    /** @param {number} cents @param {boolean} [whole] */
    const money = (cents, whole) => formatMoney(cents, currency, config.locale, whole);

    const progress =
      state.reached < tiers.length
        ? fillTemplate(messages.next, {
            amount: escapeHtml(money(state.remaining)),
            reward: tiers[state.reached]?.reward ?? '',
          })
        : messages.complete;

    this.#setHtml('message', state.owed ? messages.earned : progress);
    this.#setHtml('note', state.owed ? progress : '');
    this.#setHidden('note', !state.owed);
    this.#setHidden('choose', !state.owed);
    this.#setHidden('code', !state.codeUnlocked);

    const top = state.amounts[state.amounts.length - 1] ?? 0;
    this.#part('fill')?.style.setProperty('--succ-progress-fill', `${state.fill}%`);
    const bar = this.#part('bar');
    if (bar) {
      bar.setAttribute('aria-valuemax', String(top));
      bar.setAttribute('aria-valuenow', String(Math.min(this.subtotal, top)));
      bar.setAttribute(
        'aria-valuetext',
        htmlToText(fillTemplate(messages.value, { amount: escapeHtml(money(this.subtotal)), max: escapeHtml(money(top)) }))
      );
    }

    state.amounts.forEach((amount, index) => {
      const number = index + 1;
      const reached = number <= state.reached;
      this.#part(`milestone-${number}`)?.classList.toggle('is-reached', reached);
      this.#setHtml(`amount-${number}`, escapeHtml(money(amount, true)));
      this.#setHtml(`state-${number}`, reached ? messages.reached : messages.locked);
    });

    this.fitMilestones();
  }

  /**
   * Lists the milestones under the bar when a label won't fit under its dot. Measured rather than
   * set by a breakpoint, because the room needed depends on the ladder, the currency and the
   * shopper's text size. The class goes on a child, which Horizon's cart morph leaves alone.
   */
  fitMilestones() {
    const meter = this.querySelector('.succ-progress__meter');
    if (!meter) return;

    meter.classList.remove('is-compact');
    const spills = Array.from(meter.querySelectorAll('.succ-progress__milestone')).some((milestone) => {
      const cell = milestone.getBoundingClientRect();
      return Array.from(milestone.querySelectorAll('.succ-progress__amount, .succ-progress__label')).some((text) => {
        const box = text.getBoundingClientRect();
        // Not laid out (the thin bar hides its labels): nothing to fit.
        if (!box.width && !box.height) return false;
        return box.left < cell.left - 0.5 || box.right > cell.right + 0.5;
      });
    });
    meter.classList.toggle('is-compact', spills);
  }

  /**
   * Only a change of width can change the fit; switching layout changes the height, so reacting to
   * that would loop.
   * @param {number} width
   */
  #onResize(width) {
    if (width === this.#width) return;
    this.#width = width;
    this.fitMilestones();
  }

  /** @param {string} name */
  #part(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-part="${name}"]`));
  }

  /**
   * Only writes when the content really changes, so the live region isn't re-announced.
   * @param {string} name
   * @param {string} html
   */
  #setHtml(name, html) {
    const element = this.#part(name);
    if (element && normalizeHtml(element.innerHTML.trim()) !== normalizeHtml(html)) element.innerHTML = html;
  }

  /**
   * @param {string} name
   * @param {boolean} hidden
   */
  #setHidden(name, hidden) {
    const element = this.#part(name);
    if (element && element.hidden !== hidden) element.hidden = hidden;
  }
}

/* Cart sync --------------------------------------------------------------------------------- */

/** @type {AbortController | null} */
let inFlight = null;

/**
 * The plant state last seen, to spot the cart change that earns it.
 * @type {{ subtotal: number, owed: boolean } | null}
 */
let lastSeen = null;

let listening = false;

function cartUrl() {
  return `${window.Shopify?.routes?.root ?? '/'}cart.js`;
}

/** @returns {SuccProgress[]} */
function bars() {
  return Array.from(document.querySelectorAll(ELEMENT_NAME)).filter((element) => element instanceof SuccProgress);
}

/**
 * @param {unknown} value
 * @returns {value is { items: any[], items_subtotal_price: number, currency?: string }}
 */
function looksLikeCart(value) {
  return Boolean(
    value && typeof value === 'object' && Array.isArray(/** @type {any} */ (value).items) &&
      typeof (/** @type {any} */ (value).items_subtotal_price) === 'number'
  );
}

/**
 * @param {{ items: any[], items_subtotal_price: number, currency?: string }} cart
 */
function apply(cart) {
  const elements = bars();
  const first = elements[0];
  if (!first?.config) return;

  const claimed = isClaimed(cart, first.config.freebieProductIds ?? []);
  const subtotal = cart.items_subtotal_price;
  for (const element of elements) element.update({ subtotal, claimed, currency: cart.currency });

  const owed = Boolean(first.state?.owed);
  const previous = lastSeen;
  lastSeen = { subtotal, owed };

  // Open by itself only on the change that earns the plant: never on page load, never after a
  // removal, and not again once the shopper has closed it this session.
  if (!owed || !previous || previous.owed || subtotal <= previous.subtotal || wasDismissed()) return;
  openPicker();
}

async function refresh() {
  inFlight?.abort();
  const controller = new AbortController();
  inFlight = controller;

  try {
    const response = await fetch(cartUrl(), { signal: controller.signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    const cart = await response.json();
    if (!controller.signal.aborted) apply(cart);
  } catch (error) {
    if (/** @type {Error} */ (error)?.name !== 'AbortError') console.warn('[succ-progress] refresh failed:', error);
  } finally {
    if (inFlight === controller) inFlight = null;
  }
}

/** @param {Event} event */
function onCartChanged(event) {
  const resource = /** @type {CustomEvent} */ (event).detail?.resource;
  if (looksLikeCart(resource)) {
    inFlight?.abort();
    apply(resource);
  } else {
    refresh();
  }
}

/** The theme font usually arrives after the first layout, and its wider text can stop a label fitting. */
function refitAll() {
  for (const element of bars()) element.fitMilestones();
}

/** @param {SuccProgress} element */
function startListening(element) {
  if (!lastSeen) {
    lastSeen = { subtotal: element.subtotal, owed: Boolean(element.state?.owed) };
  }
  if (listening) return;
  listening = true;

  document.addEventListener(ThemeEvents.cartUpdate, onCartChanged);
  document.addEventListener(ThemeEvents.discountUpdate, onCartChanged);
  // snippets/add-to-cart-fallback.liquid adds early taps itself and announces them this way.
  document.addEventListener('cart:updated', onCartChanged);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
  document.fonts?.addEventListener('loadingdone', refitAll);
}

/* Picker ------------------------------------------------------------------------------------ */

/** @returns {HTMLDialogElement | null} */
function picker() {
  const dialog = document.querySelector(PICKER_SELECTOR);
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

/** @param {HTMLDialogElement} dialog @param {boolean} visible */
function toggleError(dialog, visible) {
  const error = dialog.querySelector('[data-succ-free-plant-error]');
  if (error instanceof HTMLElement) error.hidden = !visible;
}

/**
 * The picker's images ship without a src so search engines don't take them for the page's own
 * (see `snippets/succ-free-plant-picker.liquid`). Give them one the first time it opens.
 * @param {HTMLDialogElement} dialog
 */
function loadPickerImages(dialog) {
  for (const image of dialog.querySelectorAll('img[data-src]')) {
    if (!(image instanceof HTMLImageElement)) continue;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src ?? '';
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
  }
}

function openPicker() {
  const dialog = picker();
  if (!dialog) return false;
  if (!dialog.open) {
    loadPickerImages(dialog);
    toggleError(dialog, false);
    dialog.showModal();
  }
  return true;
}

/** @param {HTMLDialogElement} dialog */
function dismissPicker(dialog) {
  rememberDismissed();
  dialog.close();
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {HTMLElement} option
 */
async function addPlant(dialog, option) {
  if (dialog.getAttribute('aria-busy') === 'true') return;
  dialog.setAttribute('aria-busy', 'true');
  toggleError(dialog, false);

  try {
    // The free plant counts towards the 12 a US shopper can bring in without an import permit.
    if (!(await confirmPlantLimit({ add: 1 }))) return;

    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(option.dataset.succFreePlantVariant),
            quantity: 1,
            properties: { _freebie: 'true', _freebie_name: option.dataset.productTitle ?? '' },
          },
        ],
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

    dialog.close();

    const cart = await (await fetch(cartUrl(), { headers: { Accept: 'application/json' } })).json();
    // The same event the product form sends, so the header count, cart drawer, cart page and every
    // bar update without a reload.
    document.dispatchEvent(
      new CartAddEvent(cart, 'succ-free-plant', { source: 'succ-free-plant', itemCount: cart.item_count })
    );
  } catch (error) {
    console.warn('[succ-progress] adding the free plant failed:', error);
    toggleError(dialog, true);
  } finally {
    dialog.removeAttribute('aria-busy');
  }
}

function setUpPicker() {
  const dialog = picker();
  if (!dialog) return;

  dialog.addEventListener('click', (event) => {
    const target = event.target;
    // The dialog has no padding, so a click on the dialog itself is a click on the backdrop.
    if (target === dialog) {
      dismissPicker(dialog);
      return;
    }
    if (!(target instanceof Element)) return;

    if (target.closest('[data-succ-free-plant-close]')) {
      dismissPicker(dialog);
      return;
    }

    const option = target.closest('[data-succ-free-plant-variant]');
    if (option instanceof HTMLElement) addPlant(dialog, option);
  });

  dialog.addEventListener('cancel', rememberDismissed);
}

/* Discount code ------------------------------------------------------------------------------ */

/** @param {HTMLElement} button */
async function copyCode(button) {
  const code = button.dataset.succProgressCopy ?? '';

  try {
    await navigator.clipboard.writeText(code);
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = code;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.append(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
    } catch {
      // Nothing else to try; the code stays visible to copy by hand.
    }
    textArea.remove();
  }

  const copied = button.closest(ELEMENT_NAME)?.querySelector('[data-part="copied"]');
  if (!(copied instanceof HTMLElement)) return;
  copied.hidden = false;
  window.clearTimeout(Number(copied.dataset.timeout));
  copied.dataset.timeout = String(window.setTimeout(() => (copied.hidden = true), COPIED_DURATION));
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const opener = target.closest(OPEN_SELECTOR);
  if (opener) {
    // Without a picker on the page the link goes to the collection instead.
    if (openPicker()) event.preventDefault();
    return;
  }

  const copy = target.closest(COPY_SELECTOR);
  if (copy instanceof HTMLElement) copyCode(copy);
});

setUpPicker();

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, SuccProgress);
}
